<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t(tituloKey) }}</h1>
      <div class="toolbar"></div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" class="dx-card wide-card grid-facturas" ref="grid">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="Estado" :caption="$t('facturas.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('facturas', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TieneTicketSoporteAbierto" :caption="$t('facturas.grid.ticket_soporte_abierto')" data-type="boolean"
        cell-template="booleanTemplate" />
      <DxColumn data-field="EmpresaAgentes" :caption="$t('facturas.grid.empresa_agentes')"></DxColumn>
      <DxColumn data-field="TipoEmpresaAgentes" :caption="$t('facturas.grid.tipo_empresa_agente')" cell-template="tipoEmpresaAgenteTemplate">
        <DxLookup v-bind="getLookup('empresa_agente', 'tipo')" />
      </DxColumn>
      <DxColumn data-field="IdentificadorBorrador" :caption="$t('facturas.grid.codigo_borrador')"></DxColumn>
      <DxColumn data-field="Identificador" :caption="$t('facturas.grid.codigo_identificador')"></DxColumn>
      <DxColumn data-field="FechaEmision" :caption="$t('facturas.grid.fecha_emision')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="NombreEmisor" :caption="$t('facturas.grid.nombre_emisor')"></DxColumn>
      <DxColumn data-field="BaseImponible" :caption="$t('facturas.grid.base_imponible')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="ImporteTotal" :caption="$t('facturas.grid.importe_total')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="MotivoRechazo" :caption="$t('facturas.grid.motivo_rechazo')" cell-template="longTextWithTooltipTemplate"></DxColumn>
      <DxColumn data-field="Notas" :caption="$t('facturas.grid.notas')" cell-template="longTextWithTooltipTemplate"></DxColumn>

      <DxColumn type="buttons" :caption="$t('facturas.grid.acciones')" :fixed="true" fixed-position="right">
        <DxButton :hint="$t('facturas.grid.ver_detalle')" icon="fa-solid fa-magnifying-glass" cssClass="text-success"
          :on-click="(e: any) => verDetalle(e.row.data.Id)" />
      </DxColumn>

      <template #tipoEmpresaAgenteTemplate="{ data }">
        <div :class="[`d-inline-block rounded py-1 px-2 `, `tipoEmpresaAgente ${data.value.replace(' ', '')}`]">
            {{ $t(`empresa_agente.tipo.${data.value}`) }}
        </div>
      </template>
      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `estado-factura ${data.value}`,
        ]">
          {{ $t(`facturas.estado.${data.value}`) }}
        </div>
      </template>

      <template #longTextWithTooltipTemplate="{ data }">
        <LongTextWithTooltip :value="data.value" />
      </template>

      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>

      <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
      <template #masterDetailTemplate="{ data: factura }">
        <TramitesFacturaGrid :template-data="factura" />
        <div class="mt-4 mb-4"></div>
        <RappelsMensualesFacturaGrid :template-data="factura" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing, DxLookup, DxFormat, DxMasterDetail, DxButton } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getLookup, currencyFormat } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import { useModalService } from "@services/modal-service";
import type CustomStore from "devextreme/data/custom_store";
import type { DXLoadOptions } from "@/.shared/.api";
import TramitesFacturaGrid from "@/components/facturas/TramitesFacturaGrid.vue";
import RappelsMensualesFacturaGrid from "@/components/facturas/RappelsMensualesFacturaGrid.vue";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import FacturaModalDetail from "@components/facturas/FacturaModalDetail.vue";
import LongTextWithTooltip from "@components/LongTextWithTooltip.vue";

const props = defineProps<{
  soloFacturasPositivas: boolean;
}>();

const api = useApi();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const grid = ref<DxDataGrid>();

const tituloKey = computed(() =>
  props.soloFacturasPositivas ? "facturas.titulo_colaboradores" : "facturas.titulo_processus",
);

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (loadOptions) => {
      const q = loadOptions as DXLoadOptions;
      const filtroEmitida: unknown[] = props.soloFacturasPositivas
        ? ["ImporteTotal", ">=", 0]
        : ["ImporteTotal", "<", 0];
      if (q.filter != null && q.filter.length >= 0) {
        q.filter = [filtroEmitida, "and", q.filter];
      } else {
        q.filter = filtroEmitida;
      }
      return await api.callStandardApi("getFacturasForGrid", { showLoading: false }, q);
    },
  });
});

async function verDetalle(id: string): Promise<void> {
  await modal.showModal(FacturaModalDetail, { idFactura: id });

  grid.value?.instance?.refresh();
}

</script>

<style lang="scss" scoped></style>